import { getLowestPrice, getOriginalPrice } from '~/modules/price/parsers';
import type { ParsedUpcs } from '@nuxt-common/media-card-large/app/composables/useUpc/UpcParser';
import type { TEMPSelectedPrice } from '~/modules/price/parsers';

type RequiredParams = {
  upcData: ParsedUpcs;
  selectedUpcId: number;
  selectedPrice: TEMPSelectedPrice;
};

type FullParams = {
  selectedPrice: TEMPSelectedPrice;
  quantity: number;
  userGUID: string;
  userId: string;
  bagGuid: string;
  tier: string;
  encryptedLoyaltyId: string;
  tenderType: string;
  requiredPayload: ReturnType<typeof _mapRequiredPayload>;
};

type AllParams = RequiredParams & {
  quantity: number;
  userGUID: string;
  userId: string;
  bagGuid: string;
  tier: string;
  encryptedLoyaltyId: string;
  tenderType: string;
};

function _mapRequiredPayload({ upcData, selectedUpcId, selectedPrice }: RequiredParams) {
  const { upcs } = upcData;
  // FYI: We need to show badge for any UPC if UPC is not selected
  const selectedUpc = upcs[selectedUpcId] || Object.values(upcs)[0];
  const { priceTypeId } = selectedPrice;
  const lowestPrice = getLowestPrice(selectedPrice);
  const { department, classCode, vendorCode, markStyleCode } = selectedUpc || {};

  return {
    price: lowestPrice ?? undefined,
    dept: department?.departmentId ?? undefined,
    clazz: classCode ?? undefined,
    vendor: vendorCode ?? undefined,
    markStyle: markStyleCode || undefined, // should not be an empty string
    priceType: priceTypeId ?? undefined,
  };
}

function _mapFullPayload({ selectedPrice, quantity, userGUID, userId, bagGuid, tier, encryptedLoyaltyId, tenderType, requiredPayload }: FullParams) {
  const originalPrice = getOriginalPrice(selectedPrice);
  const { price, dept, clazz, vendor, markStyle, priceType } = requiredPayload;

  return {
    LoyaltyPrePurchaseRequestBO: {
      userGUID,
      userId,
      tier,
      bagGuid,
      encryptedLoyaltyId,
      items: [
        {
          price,
          originalPrice: originalPrice || price,
          quantity: quantity || 1,
          seqNum: 1,
          dept,
          clazz,
          priceType,
          priceSold: price,
          vendor,
          markStyle,
        },
      ],
      tenders: [
        {
          tenderType,
          tenderAmt: price,
        },
      ],
      advisory: [
        {
          code: 'ITEM',
          description: 'PPPC',
        },
      ],
    },
  };
}

export const ppcMapper = ({
  upcData,
  selectedUpcId,
  selectedPrice,
  quantity,
  userGUID,
  userId,
  bagGuid,
  tier,
  encryptedLoyaltyId,
  tenderType,
}: AllParams) => {
  const requiredPayload = _mapRequiredPayload({
    upcData,
    selectedUpcId,
    selectedPrice,
  });
  const fullPayload = _mapFullPayload({
    selectedPrice,
    requiredPayload,
    userGUID,
    userId,
    tier,
    bagGuid,
    encryptedLoyaltyId,
    quantity,
    tenderType,
  });

  return {
    requiredPayload,
    fullPayload,
  };
};

export default ppcMapper;
